import React, { useState, useEffect } from 'react';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  FileText, Target, Calendar, CheckCircle, Clock, RefreshCw, AlertCircle,
  Heart, ArrowRight, Sparkles
} from 'lucide-react';

const statusColors = {
  ACTIVE: 'bg-green-500/10 text-green-600 border-green-500/20',
  COMPLETED: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  PENDING: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  ON_HOLD: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

function GoalCard({ goal, index }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 bg-muted/30">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
              {index + 1}
            </div>
            <div>
              <CardTitle className="text-base">{goal.area || 'Goal'}</CardTitle>
              {goal.targetDate && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Calendar className="h-3 w-3" />
                  Target: {new Date(goal.targetDate).toLocaleDateString('en-AU')}
                </p>
              )}
            </div>
          </div>
          <Badge variant="outline" className={statusColors[goal.status] || ''}>
            {goal.status || 'Active'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <p className="text-sm text-muted-foreground mb-4">{goal.description}</p>
        
        {goal.strategies && goal.strategies.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Strategies</p>
            <div className="space-y-2">
              {goal.strategies.map((strategy, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-muted/30 rounded-lg">
                  <ArrowRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{strategy}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CarePlanSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div>
              <Skeleton className="h-5 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function PortalCarePlanPage() {
  const [loading, setLoading] = useState(true);
  const [carePlan, setCarePlan] = useState(null);

  const fetchCarePlan = async () => {
    setLoading(true);
    try {
      const res = await portalApi.get('/portal/participant/careplan');
      setCarePlan(res.data.data);
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarePlan();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Care Plan</h1>
            <p className="text-muted-foreground">Loading your care plan...</p>
          </div>
        </div>
        <CarePlanSkeleton />
      </div>
    );
  }

  if (!carePlan) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Care Plan</h1>
            <p className="text-muted-foreground">Your approved care plan</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchCarePlan}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">No Care Plan Available</p>
            <p className="text-sm text-muted-foreground">
              Your care plan is not yet available or has not been approved
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Care Plan</h1>
          <p className="text-muted-foreground">Your approved care plan and goals</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchCarePlan} disabled={loading} data-testid="refresh-careplan">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Plan Overview */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Your Care Plan</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Approved on {carePlan.approvedDate ? new Date(carePlan.approvedDate).toLocaleDateString('en-AU', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  }) : 'N/A'}
                </p>
                {carePlan.reviewDate && (
                  <div className="flex items-center gap-1 mt-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Next review:</span>
                    <span className="font-medium">{new Date(carePlan.reviewDate).toLocaleDateString('en-AU')}</span>
                  </div>
                )}
              </div>
            </div>
            {carePlan.goals && (
              <div className="flex items-center gap-4 px-4 py-2 bg-background/50 rounded-lg">
                <Target className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{carePlan.goals.length}</p>
                  <p className="text-xs text-muted-foreground">Goals</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {carePlan.summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{carePlan.summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Goals */}
      {carePlan.goals && carePlan.goals.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Goals ({carePlan.goals.length})
          </h2>
          <div className="grid gap-4">
            {carePlan.goals.map((goal, index) => (
              <GoalCard key={index} goal={goal} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Support Needs */}
      {carePlan.supportNeeds && carePlan.supportNeeds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              Support Needs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {carePlan.supportNeeds.map((need, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{need}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preferences */}
      {carePlan.preferences && Object.keys(carePlan.preferences).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preferences & Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              {Object.entries(carePlan.preferences).map(([key, value]) => (
                <AccordionItem key={key} value={key}>
                  <AccordionTrigger className="text-sm capitalize">
                    {key.replace(/_/g, ' ')}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-muted-foreground">{value}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
